import React, { useEffect, useRef, useState } from 'react';
import { Box, InputBase, SxProps, Typography } from '@mui/material';
import { MeasurementSystem } from '../../providers/User/User.model';

type Props = {
  leftValue?: string;
  rightValue?: string;
  onChange: (value: { leftValue: string; rightValue: string }) => void;
  type?: 'length' | 'width' | 'height';
  measurementSystem: MeasurementSystem;
  externalError?: string;
  label?: string;
  sx?: SxProps;
};

const TwImperialSystemInput: React.FC<Props> = ({
  leftValue,
  rightValue,
  onChange,
  measurementSystem,
  externalError,
  label,
  sx
}) => {
  const [error, setError] = useState<string | null>(null);
  const rightValueRef = useRef<HTMLInputElement>(null);

  const handleFeetChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;

    // Allow only integers
    if (!/^\d*$/.test(value)) {
      return;
    }

    onChange({ leftValue: value, rightValue: rightValue || '' });
  };

  const handleInchesChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value;

    if (value === '') {
      onChange({ leftValue: leftValue || '', rightValue: '' });
      return;
    }

    // Allow numbers with optional decimal point
    if (!/^\d*\.?\d*$/.test(value)) {
      return;
    }

    // allow only up to 2 decimal places
    if (value.includes('.')) {
      const decimalPlaces = value.split('.')[1].length;
      if (decimalPlaces > 2) {
        return;
      }
    }

    if (parseFloat(value) > 11.99) {
      setError('in must be less than 12');
      return;
    }
    
    // Don't try to parse until valid number is complete
    onChange({ leftValue: leftValue || '', rightValue: value });
  };

  const handleFeetKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Tab' && !e.shiftKey) {
      e.preventDefault();
      rightValueRef.current?.focus();
    }
  };

  // listen rightValue for changes
  // when rightValue is > 11.99, set error message
  useEffect(() => {
    // if rightValue contains a decimal point but not a number after it, set error message
    // ingore if rightValue is 11.99 or less
    if (rightValue && rightValue.includes('.') && !/^\d*\.\d{2}$/.test(rightValue)) {
      return;
    }

    if (measurementSystem === 'IMPERIAL' && rightValue && parseFloat(rightValue) > 11.99) {
      setError('in must be less than 12');
    } else if (measurementSystem === 'METRIC' && rightValue && parseFloat(rightValue) > 99.99) {
      setError('cm must be less than 100');
    } else {
      setError(null);
    }
  }, [rightValue]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: 'auto', ...sx }}>
      {label && (
        <Typography
          sx={{ color: '#7e858e', fontSize: '14px', padding: '0px 0px 3px 0px', fontWeight: '500' }}
        >
          {label}
        </Typography>
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderRadius: '4px',
          padding: '3px 3px',
          background: '#fff',
          // if error, add red border
          border: error ? '1px solid #FF0000' : '1px solid #E2E2EA',
          textAlign: 'center'
        }}
      >
        <InputBase
          value={leftValue || ''}
          placeholder={measurementSystem === 'IMPERIAL' ? 'ft' : '_'}
          onChange={handleFeetChange}
          onKeyDown={handleFeetKeyDown}
          inputProps={{
            inputMode: 'numeric',
            style: { width: '65%', textAlign: 'right' }
          }}
          endAdornment={<Typography sx={{ mx: 0.5, color: '#9CA3AF' }}>{'′'}</Typography>}
        />
        <InputBase
          inputRef={rightValueRef}
          value={rightValue || ''}
          placeholder={measurementSystem === 'IMPERIAL' ? 'in' : '_'}
          onChange={handleInchesChange}
          inputProps={{
            inputMode: 'text',
            max: 11.99,
            style: { width: '65%', textAlign: 'right' }
          }}
          endAdornment={<Typography sx={{ mx: 0.5, color: '#9CA3AF' }}>{'″'}</Typography>}
        />
      </Box>

      {/* label for error */}
      {(error || externalError) && (
        <Typography sx={{ color: '#FF0000', fontSize: '10px', padding: '4px 0' }}>
          {externalError || error}
        </Typography>
      )}
    </Box>
  );
};

export default TwImperialSystemInput;
